import React, { useState, useEffect } from 'react';
import fetchData from '../../functions/fetchData';
import updateData from '../../functions/updateData';
import Layout from '../../layout/Layout';
import ExportButtons from '../../components/ExportButtons';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import './NewLedger.css';

const NewLedger = () => {
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states for ledger
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    contact_person_name: '',
    address: '',
    category: 'Pending Bill',
    opening_balance: 0
  });

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // Search and pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Fetch ledgers
  const fetchLedgers = async () => {
    try {
      setLoading(true);
      await fetchData('customers', setLedgers, 'id', {});
    } catch (error) {
      console.error('Error fetching ledgers:', error);
      setError('Failed to fetch ledgers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedgers();
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Please enter customer name');
      return;
    }
    
    if (!formData.contact || formData.contact.toString().trim() === '') {
      setError('Please enter valid contact information');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Use the new customer creation API endpoint
      const response = await axios.post('/customers', {
        name: formData.name.trim(),
        contact: formData.contact.toString(),
        contact_person_name: formData.contact_person_name.trim(),
        address: formData.address.trim(),
        category: formData.category,
        opening_balance: Number(formData.opening_balance) || 0
      }, getHeaders());
      
      if (response.data && response.data.success) {
        setSuccess('Customer created successfully!');
        setFormData({ name: '', contact: '', contact_person_name: '', address: '', category: 'Pending Bill', opening_balance: 0 });
        fetchLedgers();
      } else {
        setError(response.data?.message || 'Error creating customer');
      }
    } catch (error) {
      console.error('Error creating customer:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error creating customer';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'contact' || name === 'opening_balance' ? Number(value) : value
    }));
  };

  // Handle edit
  const handleEdit = (ledger) => {
    setEditingItem(ledger);
    setEditFormData({
      name: ledger.name,
      contact: ledger.contact,
      contact_person_name: ledger.contact_person_name || '',
      address: ledger.address || '',
      category: ledger.category || 'Pending Bill',
      opening_balance: ledger.opening_balance
    });
    setShowEditModal(true);
  };

  // Handle update
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editFormData.name.trim()) {
      setError('Please enter customer name');
      return;
    }
    
    if (!editFormData.contact || editFormData.contact.toString().trim() === '') {
      setError('Please enter valid contact information');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Use the new customer update API endpoint
      const response = await axios.put(`/customers/${editingItem.id}`, {
        name: editFormData.name.trim(),
        contact: editFormData.contact.toString(),
        contact_person_name: editFormData.contact_person_name.trim(),
        address: editFormData.address.trim(),
        category: editFormData.category,
        opening_balance: Number(editFormData.opening_balance) || 0
      }, getHeaders());
      
      if (response.data && response.data.success) {
        setSuccess('Customer updated successfully!');
        setShowEditModal(false);
        setEditingItem(null);
        setEditFormData({});
        fetchLedgers();
      } else {
        setError(response.data?.message || 'Error updating customer');
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error updating customer';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (ledger) => {
    if (window.confirm(`Are you sure you want to delete "${ledger.name}"? This action cannot be undone.`)) {
      try {
        setLoading(true);
        setError('');
        
        // Use the new customer delete API endpoint
        const response = await axios.delete(`/customers/${ledger.id}`, getHeaders());
        
        if (response.data && response.data.success) {
          setSuccess('Customer deleted successfully!');
          fetchLedgers();
          // Reset pagination if current page becomes empty
          const newFilteredLedgers = ledgers.filter(l => l.id !== ledger.id);
          const newTotalPages = Math.ceil(newFilteredLedgers.length / itemsPerPage);
          if (currentPage > newTotalPages && newTotalPages > 0) {
            setCurrentPage(newTotalPages);
          }
        } else {
          setError(response.data?.message || 'Error deleting customer');
        }
      } catch (error) {
        console.error('Error deleting customer:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Error deleting customer';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle edit form input changes
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: name === 'contact' || name === 'opening_balance' ? Number(value) : value
    }));
  };

  // Close modal
  const closeModal = () => {
    setShowEditModal(false);
    setEditingItem(null);
    setEditFormData({});
  };

  // Filter ledgers based on search term
  const filteredLedgers = ledgers.filter(ledger =>
    ledger.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ledger.contact.toString().includes(searchTerm) ||
    ledger.id.toString().includes(searchTerm)
  );

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLedgers = filteredLedgers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredLedgers.length / itemsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Calculate stats
  const totalLedgers = ledgers.length;
  const totalOpeningBalance = ledgers.reduce((sum, ledger) => sum + (ledger.opening_balance || 0), 0);

  return (
    <Layout>
      <div className="ledger-container">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Ledger Management</h4>
          
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="alert alert-danger alert-custom">
            <i className="zmdi zmdi-close-circle mr-2"></i>
            {error}
          </div>
        )}
        
        {success && (
          <div className="alert alert-success alert-custom">
            <i className="zmdi zmdi-check-circle mr-2"></i>
            {success}
          </div>
        )}

        {/* Stats Cards */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="ledger-stats bg-primary text-white">
              <h4>{totalLedgers}</h4>
              <p>Total Ledgers</p>
            </div>
          </div>
          <div className="col-md-6">
            <div className="ledger-stats bg-success text-white">
              <h4>฿{totalOpeningBalance.toLocaleString()}</h4>
              <p>Total Opening Balance</p>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Form Section */}
          <div className="col-12 mb-4">
            <div className="ledger-card">
              <div className="ledger-card-header bg-primary text-white">
                <h5 className="ledger-card-title mb-0">
                  <i className="zmdi zmdi-plus-circle mr-2"></i> Add New Ledger
                </h5>
              </div>
              <div className="ledger-card-body">
              <form onSubmit={handleSubmit} className="ledger-form">
                <div className="row">
                  <div className="col-md-3 col-sm-6">
                    <div className="form-group">
                      <label className="control-label">Ledger Name *</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter ledger name"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-3 col-sm-6">
                    <div className="form-group">
                      <label className="control-label">Contact Number *</label>
                      <input
                        type="number"
                        name="contact"
                        className="form-control"
                        value={formData.contact}
                        onChange={handleInputChange}
                        placeholder="Enter contact number"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-3 col-sm-6">
                    <div className="form-group">
                      <label className="control-label">Contact Person Name</label>
                      <input
                        type="text"
                        name="contact_person_name"
                        className="form-control"
                        value={formData.contact_person_name}
                        onChange={handleInputChange}
                        placeholder="Enter contact person name"
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-3 col-sm-6">
                    <div className="form-group">
                      <label className="control-label">Category *</label>
                      <select
                        name="category"
                        className="form-control"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="Pending Bill">Pending Bill</option>
                        <option value="Sponsor">Sponsor</option>
                        <option value="Advance">Advance</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="control-label">Address</label>
                      <textarea
                        name="address"
                        className="form-control"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Enter address"
                        rows="2"
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-3 col-sm-6">
                    <div className="form-group">
                      <label className="control-label">Opening Balance</label>
                      <input
                        type="number"
                        name="opening_balance"
                        className="form-control"
                        value={formData.opening_balance}
                        onChange={handleInputChange}
                        placeholder="Enter opening balance"
                        min="0"
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-3 col-sm-6">
                    <div className="form-group">
                      <label className="control-label">&nbsp;</label>
                      <button 
                        type="submit" 
                        className="btn btn-submit btn-block"
                        disabled={loading}
                      >
                        {loading && <span className="loading-spinner"></span>}
                        Add Ledger
                      </button>
                    </div>
                  </div>
                </div>
              </form>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="col-12">
            <div className="ledger-card">
              <div className="ledger-card-header bg-info text-white">
                <h5 className="ledger-card-title mb-0">
                  <i className="zmdi zmdi-view-list mr-2"></i> Existing Ledgers ({filteredLedgers.length})
                </h5>
              </div>
              <div className="ledger-card-body">
              
              {loading && (
                <div className="text-center py-4">
                  <span className="loading-spinner"></span>
                  <span className="ml-2">Loading ledgers...</span>
                </div>
              )}
              
              {!loading && filteredLedgers.length === 0 && searchTerm && (
                <div className="empty-state">
                  <i className="zmdi zmdi-search"></i>
                  <h5>No Results Found</h5>
                  <p>No ledgers match your search criteria</p>
                </div>
              )}
              
              {!loading && ledgers.length === 0 && !searchTerm && (
                <div className="empty-state">
                  <i className="zmdi zmdi-account-balance-wallet"></i>
                  <h5>No Ledgers Found</h5>
                  <p>Add your first ledger to get started</p>
                </div>
              )}
              
              {!loading && filteredLedgers.length > 0 && (
                <>
                  <div className="ledger-table-panel">
                    <table className="table table-striped ledger-table">
                      <thead>
                        <tr>
                          <td colSpan={3}>
                            <div className="search-box">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search ledgers..."
                                value={searchTerm}
                                onChange={handleSearch}
                              />
                            </div>
                          </td>
                          <td colSpan={3} style={{ textAlign: 'right' }}>
                            <ExportButtons
                              data={filteredLedgers}
                              filename="ledgers"
                              title="Ledgers Report"
                              columns={[
                                { key: 'id', label: 'ID' },
                                { key: 'name', label: 'Name' },
                                { key: 'contact', label: 'Contact' },
                                { key: 'contact_person_name', label: 'Contact Person' },
                                { key: 'address', label: 'Address' },
                                { key: 'category', label: 'Category' },
                                { key: 'opening_balance', label: 'Opening Balance', formatter: (value) => `฿${value.toLocaleString()}` },
                                { key: 'created_on', label: 'Created Date' }
                              ]}
                              size="sm"
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Contact</th>
                          <th>Contact Person</th>
                          <th>Address</th>
                          <th>Category</th>
                          <th>Opening Balance</th>
                          <th>Created Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentLedgers.map((ledger) => (
                          <tr key={ledger.id}>
                            <td>{ledger.id}</td>
                            <td><strong>{ledger.name}</strong></td>
                            <td>{ledger.contact}</td>
                            <td>{ledger.contact_person_name || '-'}</td>
                            <td>{ledger.address || '-'}</td>
                            <td>
                              <span className={`badge badge-${ledger.category === 'Pending Bill' ? 'primary' : ledger.category === 'Sponsor' ? 'info' : ledger.category === 'Advance' ? 'warning' : 'secondary'}`}>
                                {ledger.category || 'Pending Bill'}
                              </span>
                            </td>
                            <td>
                              {ledger.opening_balance > 0 ? (
                                <span className="text-success">฿{ledger.opening_balance.toLocaleString()}</span>
                              ) : (
                                <span className="text-muted">฿0</span>
                              )}
                            </td>
                            <td>{new Date(ledger.created_on).toLocaleDateString()}</td>
                            <td className="action-buttons">
                              <button 
                                className="btn btn-info btn-xs" 
                                title="Edit"
                                onClick={() => handleEdit(ledger)}
                              >
                                <i className="zmdi zmdi-edit"></i>
                              </button>
                              <button 
                                className="btn btn-danger btn-xs ml-1" 
                                title="Delete"
                                onClick={() => handleDelete(ledger)}
                              >
                                <i className="zmdi zmdi-delete"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="pagination-wrapper mt-3">
                      <nav>
                        <ul className="pagination justify-content-center">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          
                          {[...Array(totalPages)].map((_, index) => {
                            const pageNumber = index + 1;
                            return (
                              <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                                <button 
                                  className="page-link" 
                                  onClick={() => handlePageChange(pageNumber)}
                                >
                                  {pageNumber}
                                </button>
                              </li>
                            );
                          })}
                          
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                      
                      <div className="pagination-info text-center mt-2">
                        <small className="text-muted">
                          Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredLedgers.length)} of {filteredLedgers.length} entries
                        </small>
                      </div>
                    </div>
                  )}
                </>
              )}
              </div>
            </div>
          </div>
        </div>

        {/* Edit Modal */}
        {showEditModal && (
          <div className="modal" style={{ display: 'block', zIndex: 9999, position: 'fixed' }}>
            <div className="modal-backdrop" onClick={closeModal} style={{ zIndex: 9998 }}></div>
            <div className="modal-dialog" style={{ zIndex: 10000 }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h4>Edit Customer</h4>
                  <button type="button" className="close" onClick={closeModal}>
                    <span>&times;</span>
                  </button>
                </div>
                <form onSubmit={handleUpdate}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label className="control-label">Customer Name *</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        value={editFormData.name || ''}
                        onChange={handleEditInputChange}
                        placeholder="Enter customer name"
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="control-label">Contact Information *</label>
                      <input
                        type="text"
                        name="contact"
                        className="form-control"
                        value={editFormData.contact || ''}
                        onChange={handleEditInputChange}
                        placeholder="Enter contact information"
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="control-label">Contact Person Name</label>
                      <input
                        type="text"
                        name="contact_person_name"
                        className="form-control"
                        value={editFormData.contact_person_name || ''}
                        onChange={handleEditInputChange}
                        placeholder="Enter contact person name"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="control-label">Address</label>
                      <textarea
                        name="address"
                        className="form-control"
                        value={editFormData.address || ''}
                        onChange={handleEditInputChange}
                        placeholder="Enter address"
                        rows="2"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="control-label">Category *</label>
                      <select
                        name="category"
                        className="form-control"
                        value={editFormData.category || 'Pending Bill'}
                        onChange={handleEditInputChange}
                        required
                      >
                        <option value="Pending Bill">Pending Bill</option>
                        <option value="Sponsor">Sponsor</option>
                        <option value="Advance">Advance</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label className="control-label">Opening Balance</label>
                      <input
                        type="number"
                        name="opening_balance"
                        className="form-control"
                        value={editFormData.opening_balance || 0}
                        onChange={handleEditInputChange}
                        placeholder="Enter opening balance"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-default" onClick={closeModal}>
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading && <span className="loading-spinner"></span>}
                      Update Customer
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default NewLedger;
